import React, { useEffect, useState } from "react";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { Button, ButtonGroup, Form, Spinner } from "react-bootstrap";
import ConfirmationModal from "../ConfirmationModal/ConfirmationModal";
import { useNavigate } from "react-router-dom";
import './Playlist.css'; // Assuming you save your CSS styles in this file
import EditPlaylistModal from "../../../components/Model/EditPlaylistModel"
import SetPlaylistPriorityModel from "../../../components/Model/SetPlaylistPriorityModel";
import { extractObjectId } from "../../../utils/utils";
import PageTargetSelector from "../../../components/common/PageTargetSelector";
import usePageTargetSelection from "../../../hooks/usePageTargetSelection";

function Playlist() {
  const pageTarget = usePageTargetSelection();
  const targetParams = pageTarget.selectedTargetParams;
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [playlistToDelete, setPlaylistToDelete] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [playlistEditData, setPlaylistEditData] = useState({});
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const [priorityPlaylistData, setPriorityPlaylistData] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [limit, setLimit] = useState(20);
  const [refreshTick, setRefreshTick] = useState(0);

  const navigate = useNavigate();

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage((prevPage) => prevPage + 1);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage((prevPage) => prevPage - 1);
    }
  };

  useEffect(() => {
    const fetchAllAdminPlaylist = async (page, limit, search) => {
      setLoading(true);

      try {
        const params = { limit, page, ...targetParams };
        if (search) params.search = search;
        let response = await allAPiServicesCall.getAllAdminPlaylist(
          params,
          getAuthConfig()
        );

        const { allPlaylists, totalPages,  } = response.data

        if (Array.isArray(allPlaylists)) {
          setPlaylists(allPlaylists);
          setTotalPages(totalPages)
        } else {
          console.warn("No data found or data is not an array");
          setError("No data found or data is not an array");
        }
      } catch (error) {
        console.error("Error fetching playlists:", error);
        setError("Error fetching playlists");
      } finally {
        setLoading(false);
      }
    };

    // Debounce so we don't fire a request on every keystroke while typing.
    const timer = setTimeout(() => {
      fetchAllAdminPlaylist(currentPage, limit, searchTerm);
    }, searchTerm ? 300 : 0);
    return () => clearTimeout(timer);
  }, [currentPage, limit, targetParams.pageType, targetParams.genreSlug, refreshTick, searchTerm]);

  const handleCreatePlaylist = () => {
    navigate("/create_playlist");
  };

  const handleDeletePlaylist = (playlistId) => {
    setPlaylistToDelete(playlistId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!playlistToDelete) return;

    try {
      await allAPiServicesCall.deleteAdminPlaylist(
        { playlist_id: playlistToDelete },
        getAuthConfig()
      );

      setPlaylists(playlists.filter((playlist) => extractObjectId(playlist._id) !== playlistToDelete));

      setShowDeleteModal(false);
      setPlaylistToDelete(null);
    } catch (error) {
      console.error("Error deleting playlist:", error);
    }
  };

  const handleEditPlayList = (playlistID) => {
    setShowEditModal(true)
    setPlaylistEditData(playlistID)
  }

  const handlePlaylistUpdated = (updatedPlaylist) => {
    setPlaylists(playlists.map(p =>
      extractObjectId(p._id) === extractObjectId(updatedPlaylist._id)
        ? { ...p, ...updatedPlaylist }
        : p
    ));
  }

  const handleSearchTermChange = (e) => {
    setSearchTerm(e.target.value);
    // A new search shouldn't keep you on a deep page that may not exist for
    // the filtered set.
    setCurrentPage(1);
  };

  const handleToggleTopPlaylist = async (playlist_id, isTopPlaylist) => {
    try {
      await allAPiServicesCall.toggleTopPlaylist(
        { playlist_id, isTopPlaylist, ...targetParams },
        getAuthConfig()
      );

      // Refetch so the new priority shows up on the toggled card AND other
      // cards reflect any renumbering (gap-fill when toggling OFF, normalize
      // pass on read).
      setRefreshTick((t) => t + 1);
    } catch (error) {
      console.error("Error toggling top playlist:", error);
    }
  };

  const handleToggleExplorePlaylist = async (playlist_id, isExplorePlaylist) => {
    try {
      await allAPiServicesCall.toggleExplorePlaylist(
        { playlist_id, isExplorePlaylist, ...targetParams },
        getAuthConfig()
      );

      setRefreshTick((t) => t + 1);
    } catch (error) {
      console.error("Error toggling explore playlist:", error);
    }
  };

  return (
    <CommonLayout>
      <div className="container my-5 pb-5 pt-2">
        <h3 style={{
          textAlign: "center",
          fontFamily: "'Montserrat', sans-serif",
          fontSize: "2.8rem",
          fontWeight: "600",
          letterSpacing: "1.5px",
          textShadow: "3px 3px 8px rgba(0, 0, 0, 0.3)",
          marginBottom: "10px",
          background: "-webkit-linear-gradient(to right, #4c6ef5, #3a86ff)",
          WebkitBackgroundClip: "text",
        }} className="playlist-heading">ALL PLAYLIST</h3>

        <Form.Group controlId="searchAlbum">
          <Form.Control
            type="text"
            placeholder="Search for a playlist..."
            value={searchTerm}
            onChange={handleSearchTermChange}
            className="search-album-input mb-4"
          />
        </Form.Group>

        <PageTargetSelector
          pageTarget={pageTarget}
          subtitle="Switch between Homepage and Genre targets before assigning playlist placements."
        />

        {loading && playlists.length === 0 ? (
          <div className="spinner-container">
            <Spinner animation="border" role="status">
              <span className="sr-only">Loading...</span>
            </Spinner>
          </div>
        ) : error ? (
          <p>{error}</p>
        ) : playlists.length === 0 ? (
          <div className="no-playlists">
            <p>No playlists found. You can create a new playlist.</p>
            <Button variant="primary" onClick={handleCreatePlaylist}>
              Create Playlist
            </Button>
          </div>
        ) : (
          <div className="playlist-card-container">
            {playlists.map((playlist) => (
              <div key={playlist._id} className="playlist-card">
                <img
                  className="playlist-card-image"

                  src={playlist.playlist_image}
                  alt={playlist.title}
                />
                <div className="playlist-card-content">
                  <h3 className="playlist-card-title text-center">{playlist.playlist_name}</h3>
                  <p className="playlist-card-description text-center">
                    Total Songs: {playlist.song_count}
                  </p>

                  <ButtonGroup className="playlist-button-group">
                    <Button
                      variant="primary"
                      onClick={() => {
                        navigate(`/playlist/${extractObjectId(playlist._id)}`);
                      }}
                    >
                      View
                    </Button>
                    <Button
                      variant="info"
                      onClick={() => handleEditPlayList(playlist)}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="danger"
                      onClick={() => handleDeletePlaylist(extractObjectId(playlist._id))}
                    >
                      Delete
                    </Button>
                  </ButtonGroup>

                  <ButtonGroup className="playlist-button-group mt-2">
                    <Button
                      variant={playlist.isTopPlaylist ? "success" : "secondary"}
                      size="sm"
                      onClick={() => handleToggleTopPlaylist(extractObjectId(playlist._id), !playlist.isTopPlaylist)}
                    >
                      Top
                      {playlist.isTopPlaylist && playlist.topPlacementPriority != null
                        ? ` #${playlist.topPlacementPriority}`
                        : ""}
                    </Button>
                    <Button
                      variant={playlist.isExplorePlaylist ? "success" : "secondary"}
                      size="sm"
                      onClick={() => handleToggleExplorePlaylist(extractObjectId(playlist._id), !playlist.isExplorePlaylist)}
                    >
                      Explore
                      {playlist.isExplorePlaylist && playlist.explorePlacementPriority != null
                        ? ` #${playlist.explorePlacementPriority}`
                        : ""}
                    </Button>
                    <Button
                      variant="warning"
                      size="sm"
                      disabled={!playlist.isTopPlaylist && !playlist.isExplorePlaylist}
                      onClick={() => {
                        setPriorityPlaylistData(playlist);
                        setShowPriorityModal(true);
                      }}
                    >
                      ★ Priority
                    </Button>
                  </ButtonGroup>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <ConfirmationModal
        show={showDeleteModal}
        handleClose={() => setShowDeleteModal(false)}
        onConfirm={confirmDelete}
        message="Are you sure you want to delete this playlist?"
      />



      <EditPlaylistModal
        show={showEditModal}
        handleClose={() => setShowEditModal(false)}
        playlistDetails={playlistEditData}
        onPlaylistUpdated={handlePlaylistUpdated}
      />

      <SetPlaylistPriorityModel
        show={showPriorityModal}
        handleClose={() => {
          setShowPriorityModal(false);
          setPriorityPlaylistData(null);
        }}
        playlistDetails={priorityPlaylistData}
        handleFormSubmit={() => setRefreshTick((t) => t + 1)}
      />

          {playlists.length > 0 && (
            <div className="pagination-container">
              <Button onClick={handlePreviousPage} disabled={currentPage === 1}>
                Previous
              </Button>
              <span>
                {currentPage} / {totalPages}
              </span>
              <Button
                onClick={handleNextPage}
                disabled={currentPage === totalPages}
              >
                Next
              </Button>
            </div>
          )}
    </CommonLayout>



  );
}

export default Playlist;
